import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';
import { Device } from '@capacitor/device';
import { hasPrivacyAgreement } from '@/utils/privacyConsent';

/** Android 首屏兜底状态栏高度（env 常为 0，getInfo 冷启动也可能为 0） */
const DEFAULT_ANDROID_INSET_PX = 28;

function setInsetVariables(insetPx) {
  const value = Math.max(0, Number(insetPx) || 0);
  document.documentElement.style.setProperty('--xpz-safe-inset-top', `${value}px`);
  document.documentElement.style.setProperty('--xpz-header-offset', `${48 + value}px`);
  return value;
}

function isAndroidNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

function isIOSNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/**
 * 同步预置安全区（在 Vue 挂载前调用，避免首屏被状态栏遮挡）
 * 不调用任何 Device API，合规安全
 */
export function applyProvisionalSafeArea() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  document.body.classList.add('capacitor-app');

  if (isAndroidNative()) {
    document.body.classList.add('need-safe-area');
    setInsetVariables(DEFAULT_ANDROID_INSET_PX);
    console.log('[safe-area] provisional android inset applied', DEFAULT_ANDROID_INSET_PX);
  }
}

async function readEnvSafeInsetPx() {
  try {
    const tmp = document.createElement('div');
    tmp.style.cssText = 'position:absolute;top:-9999px;padding-top:env(safe-area-inset-top);';
    document.body.appendChild(tmp);
    const px = parseFloat(getComputedStyle(tmp).paddingTop) || 0;
    document.body.removeChild(tmp);
    return px;
  } catch (e) {
    return 0;
  }
}

async function readStatusBarHeightPx() {
  try {
    const sbInfo = await StatusBar.getInfo();
    return sbInfo && sbInfo.height ? Number(sbInfo.height) : 0;
  } catch (e) {
    return 0;
  }
}

async function isXiaomiByDevice() {
  if (!hasPrivacyAgreement()) {
    return false;
  }
  try {
    if (!Device.getInfo) return false;
    const info = await Device.getInfo();
    const combined = `${info.manufacturer || ''} ${info.model || ''}`.toLowerCase();
    return /xiaomi|redmi|poco|miui/i.test(combined);
  } catch (e) {
    return false;
  }
}

/**
 * 精确测量并应用安全区（可多次调用，用于冷启动后 StatusBar 就绪时刷新）
 * 仅在用户已同意隐私政策后才会调用 Device.getInfo（用于小米机型判断）
 */
export async function applyNativeSafeArea() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  document.body.classList.add('capacitor-app');

  try {
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setStyle({ style: 'DARK' });
    await StatusBar.setBackgroundColor({ color: '#FEFEFE' });
  } catch (e) {
    console.warn('[safe-area] StatusBar setup failed', e);
  }

  const envPx = await readEnvSafeInsetPx();
  const statusBarPx = await readStatusBarHeightPx();
  let insetPx = envPx > 0 ? envPx : statusBarPx;

  if (isAndroidNative() && insetPx <= 0) {
    const isXiaomi = hasPrivacyAgreement() ? await isXiaomiByDevice() : false;
    insetPx = isXiaomi ? 28 : DEFAULT_ANDROID_INSET_PX;
    console.log('[safe-area] android fallback inset', { insetPx, isXiaomi });
  }

  if (isIOSNative() && insetPx <= 0 && envPx <= 0) {
    insetPx = 20;
  }

  if (insetPx > 0 || isAndroidNative()) {
    document.body.classList.add('need-safe-area');
    setInsetVariables(insetPx > 0 ? insetPx : DEFAULT_ANDROID_INSET_PX);
    console.log('[safe-area] applied', { envPx, statusBarPx, insetPx });
  }

  window.dispatchEvent(new CustomEvent('xpz-safe-area-applied', {
    detail: { insetPx: insetPx > 0 ? insetPx : DEFAULT_ANDROID_INSET_PX },
  }));
}

export function setupSafeAreaListeners() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const refresh = () => {
    if (!hasPrivacyAgreement()) {
      applyProvisionalSafeArea();
      return;
    }
    applyNativeSafeArea().catch((e) => {
      console.warn('[safe-area] refresh failed', e);
    });
  };

  document.addEventListener('resume', refresh);
  window.addEventListener('orientationchange', refresh);
  window.addEventListener('resize', refresh);

  window.addEventListener('xpz-privacy-agreed', refresh);
}
